import {
  Calendar,
  Check,
  Clock,
  Link as LinkIcon,
  ListTodo,
  Paperclip,
  Tag,
  Timer,
} from "lucide-react";
import { useUIPreferences } from "../../../contexts/UIPreferencesContext";

const displayOptions = [
  {
    key: "showScheduleDatesChip",
    icon: <Calendar size={16} />,
    label: "Show Schedule Dates",
    description: "Scheduled date on task cards",
  },
  {
    key: "showSubtasksChip",
    icon: <ListTodo size={16} />,
    label: "Show Subtasks",
    description: "Subtask progress on task cards",
  },
  {
    key: "showAttachmentsChip",
    icon: <Paperclip size={16} />,
    label: "Show Attachments",
    description: "Attachment count on task cards",
  },
  {
    key: "showLinksChip",
    icon: <LinkIcon size={16} />,
    label: "Show Links",
    description: "Link count on task cards",
  },
  {
    key: "showDurationChip",
    icon: <Timer size={16} />,
    label: "Show Duration",
    description: "Estimated duration on task cards",
  },
  {
    key: "showTagsChip",
    icon: <Tag size={16} />,
    label: "Show Tags",
    description: "Tag labels on task cards",
  },
  {
    key: "showCreatedDateChip",
    icon: <Clock size={16} />,
    label: "Show Created Date",
    description: "Days since task was created",
  },
];

function PreviewCard() {
  const { preferences } = useUIPreferences();

  const anyChipVisible =
    preferences.showScheduleDatesChip ||
    preferences.showSubtasksChip ||
    preferences.showAttachmentsChip ||
    preferences.showLinksChip ||
    preferences.showDurationChip ||
    preferences.showTagsChip ||
    preferences.showCreatedDateChip;

  return (
    <div
      aria-label="Preview card"
      className="bg-warm-bg-card p-3 rounded-card border border-warm-border shadow-soft-md"
    >
      <div className="flex items-start gap-2.5">
        <div className="flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center border-warm-text-tertiary mt-0.5">
          <Check size={12} className="text-transparent" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm leading-relaxed text-warm-text-primary">
            Example task
          </p>
          {anyChipVisible && (
            <div className="flex items-center gap-2 mt-2 text-xs flex-wrap">
              {preferences.showScheduleDatesChip && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-warm-bg-elevated text-warm-text-secondary rounded">
                  <Calendar size={11} />
                  Feb 23
                </span>
              )}
              {preferences.showSubtasksChip && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-warm-bg-elevated text-warm-text-secondary rounded">
                  <ListTodo size={11} />
                  2/3
                </span>
              )}
              {preferences.showAttachmentsChip && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-warm-bg-elevated text-warm-text-secondary rounded">
                  <Paperclip size={11} />
                  2 files
                </span>
              )}
              {preferences.showLinksChip && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-warm-bg-elevated text-warm-text-secondary rounded">
                  <LinkIcon size={11} />
                  1 link
                </span>
              )}
              {preferences.showDurationChip && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-warm-bg-elevated text-warm-text-secondary rounded">
                  <Timer size={11} />
                  1h 30m
                </span>
              )}
              {preferences.showTagsChip && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-warm-bg-elevated text-warm-text-secondary rounded">
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: "var(--color-accent-amber)" }}
                  />
                  Urgent
                </span>
              )}
              {preferences.showCreatedDateChip && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-warm-bg-elevated text-warm-text-secondary rounded">
                  3 days
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function GeneralSettings() {
  const { preferences, setPreferences } = useUIPreferences();

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold text-warm-text-primary">Card Display</h3>

      <PreviewCard />

      <div className="rounded-xl border border-warm-border bg-warm-bg-elevated divide-y divide-warm-border overflow-hidden">
        {displayOptions.map((option) => {
          const isSelected = !!preferences[option.key as keyof typeof preferences];
          return (
            <button
              key={option.key}
              role="switch"
              aria-checked={isSelected}
              onClick={() => setPreferences({ [option.key]: !isSelected })}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-warm-border-subtle/50 transition-colors"
            >
              <div className={`flex-shrink-0 ${isSelected ? "text-warm-blue" : "text-warm-text-tertiary"}`}>
                {option.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-warm-text-primary leading-tight">{option.label}</div>
                <div className="text-xs text-warm-text-tertiary leading-tight mt-0.5">{option.description}</div>
              </div>
              <div className={`flex-shrink-0 relative w-9 h-5 rounded-full transition-colors duration-200 ${isSelected ? "bg-warm-blue" : "bg-warm-border"}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${isSelected ? "translate-x-4" : "translate-x-0.5"}`} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
